import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { TaskCommandPalette } from "@/components/tasks/TaskCommandPalette";

/**
 * Global Task Command Palette.
 *
 * Mounted once at app root so any screen can call ``openTaskPalette({...})``
 * to launch ad-hoc tasks. There is no keyboard shortcut for the palette
 * itself — Cmd/Ctrl-K is owned by the OS-wide global search. The palette
 * is invoked from explicit "New task" affordances (Mission Control,
 * Client detail header, etc.).
 */
interface PaletteOptions {
  initialFaClientId?: string;
  initialCatalogueId?: string;
  /** Seeded into POST /tasks ``inputs_resolved`` (e.g. per-plan uuid). */
  initialInputsResolved?: Record<string, unknown>;
  /** Force-attach the new task to a specific mission. Skips the Mission step. */
  lockMissionId?: string;
  onLaunched?: (taskId: string, missionId: string | null) => void;
}

interface PaletteContextValue {
  open: (opts?: PaletteOptions) => void;
}

const TaskPaletteContext = createContext<PaletteContextValue | null>(null);

const TASK_PALETTE_NOOP: PaletteContextValue = {
  open: () => {
    if (import.meta.env.DEV) {
      console.warn(
        "[TaskPalette] open() called without TaskPaletteProvider — palette unavailable (often transient during hot reload)"
      );
    }
  },
};

export function useTaskPalette(): PaletteContextValue {
  return useContext(TaskPaletteContext) ?? TASK_PALETTE_NOOP;
}

/** Same as useTaskPalette but returns null if no provider is mounted. */
export function useTaskPaletteOptional(): PaletteContextValue | null {
  return useContext(TaskPaletteContext);
}

export function TaskPaletteProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [opts, setOpts] = useState<PaletteOptions>({});

  const open = useCallback((next?: PaletteOptions) => {
    setOpts(next ?? {});
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo<PaletteContextValue>(() => ({ open }), [open]);

  return (
    <TaskPaletteContext.Provider value={value}>
      {children}
      <TaskCommandPalette
        open={isOpen}
        onClose={close}
        onLaunched={opts.onLaunched}
        initialFaClientId={opts.initialFaClientId}
        initialCatalogueId={opts.initialCatalogueId}
        initialInputsResolved={opts.initialInputsResolved}
        lockMissionId={opts.lockMissionId}
      />
    </TaskPaletteContext.Provider>
  );
}
